// src/utils/saveSmartAlertSettings.js
import { getAuth } from "firebase/auth";

/**
 * Small helper to get the current Firebase ID token.
 *
 * Firebase refreshes the token when needed. All Smart Alert Settings and
 * review requests use this same authenticated API boundary.
 */
async function getAuthToken() {
  const user = getAuth().currentUser;

  if (!user) {
    const error = new Error("User not authenticated");
    error.code = "smart_alert_login_required";
    throw error;
  }

  return await user.getIdToken();
}

/**
 * Shared Smart Alert Settings API fetch helper.
 *
 * Retry policy:
 * - retries network failures
 * - retries 5xx responses
 * - does NOT retry 4xx/application validation responses
 *
 * This matters for POST routes such as /alerts/request-review. The backend is
 * idempotent for a pending review request, but the client should still avoid
 * unnecessary duplicate application requests.
 */
async function apiFetch(
  path,
  {
    method = "GET",
    headers = {},
    body,
  } = {},
  retries = 1
) {
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

  if (!base) {
    const error = new Error("Backend URL is not configured.");
    error.code = "smart_alert_backend_not_configured";
    throw error;
  }

  const url = `${base}${path}`;

  let lastErr = null;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    let res;

    try {
      res = await fetch(url, {
        method,
        headers,
        body,
      });
    } catch (networkError) {
      lastErr = networkError;

      if (attempt < retries) {
        continue;
      }

      throw networkError;
    }

    let data = null;

    try {
      data = await res.json();
    } catch {
      data = {
        success: false,
        error: "Invalid server response (non-JSON)",
      };
    }

    if (!res.ok || data?.success === false) {
      const msg =
        data?.error ||
        data?.message ||
        `Request failed (${res.status})`;

      const error = new Error(msg);
      error.status = res.status;
      error.code = data?.error_code || "";
      error.data = data;
      error.payload = data;

      const retryableServerError =
        res.status >= 500 &&
        res.status <= 599;

      if (
        retryableServerError &&
        attempt < retries
      ) {
        lastErr = error;
        continue;
      }

      throw error;
    }

    return data;
  }

  throw lastErr || new Error("Request failed");
}

/**
 * Loads Smart Alert contacts + dynamically derived deny-list information.
 *
 * Returns:
 * {
 *   success,
 *   contacts,
 *   deny_info
 * }
 */
export async function loadSmartAlertSettings() {
  const token = await getAuthToken();

  return await apiFetch(
    "/alerts/load-configuration",
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
    1
  );
}

/**
 * Saves the Smart Alert contact list.
 *
 * @param {Array} contacts
 *   [{ name, email, phone, message }]
 *
 * Returns:
 * {
 *   success,
 *   message,
 *   contacts,
 *   deny_info,
 *   billing
 * }
 */
export async function saveSmartAlertSettings(contacts) {
  const token = await getAuthToken();

  return await apiFetch(
    "/alerts/save-configuration",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        contacts,
      }),
    },
    1
  );
}

/**
 * Creates or reuses the authenticated user's deny-list review request.
 *
 * The backend:
 * - verifies the destination belongs to a saved Smart Alert contact
 * - verifies the destination is still blocked
 * - refuses sender override for recipient-controlled opt-outs/complaints
 * - stores one deterministic review request
 * - sends the best-effort admin/support notification
 *
 * @param {Object} params
 * @param {"email"|"phone"} params.kind
 * @param {string} params.value
 * @param {string|null} [params.note]
 */
export async function requestSmartAlertReview({
  kind,
  value,
  note = null,
}) {
  const token = await getAuthToken();

  return await apiFetch(
    "/alerts/request-review",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        kind,
        value,
        ...(note ? { note } : {}),
      }),
    },
    1
  );
}

/**
 * Loads the current review state for one blocked destination.
 *
 * @param {Object} params
 * @param {"email"|"phone"} params.kind
 * @param {string} params.value
 */
export async function loadSmartAlertReviewStatus({
  kind,
  value,
}) {
  const token = await getAuthToken();

  const params = new URLSearchParams({
    kind,
    value,
  });

  return await apiFetch(
    `/alerts/review-status?${params.toString()}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
    1
  );
}

// Optional grouped export for consumers that prefer one API object.
const SmartAlertSettingsAPI = {
  loadSmartAlertSettings,
  saveSmartAlertSettings,
  requestSmartAlertReview,
  loadSmartAlertReviewStatus,
};

export default SmartAlertSettingsAPI;
